import { useQueryClient } from '@tanstack/react-query';

import type { OrderCardsParams } from '@/api/cardOrders/types';
import { buildCardOrderRequest } from '@/api/cardOrders/utils';
import { CardOrderDetails, CardOrdersList, type CardOrdersListType, CardOrdersResponse } from '@/api/schema';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { axiosInstance } from '@/lib/api/axios';
import { useAccountStore } from '@/stores/accountStore';

// POST: /card-orders
export const orderCards = async (params: OrderCardsParams) => {
    const { userProfile } = useAccountStore.getState();

    if (!userProfile) {
        throw new Error('User profile not found');
    }

    const orderRequest = buildCardOrderRequest(params);

    const parsedRequest = CardOrderDetails.safeParse(orderRequest);

    if (!parsedRequest.success) {
        throw new Error(`Invalid request data: ${parsedRequest.error.message}`);
    }

    const { data } = await axiosInstance.post('/card-orders', parsedRequest.data, {
        validateStatus: status => status === 201,
    });

    return data;
};

export const useOrderCards = () => {
    const queryClient = useQueryClient();
    return useApiMutation({
        mutationFn: orderCards,
        validator: CardOrdersResponse,
        options: {
            onSuccess: () => queryClient.invalidateQueries({ queryKey: ['card-orders'] }),
        },
    });
};

// GET: /card-orders
const fetchCardOrders = async (): Promise<CardOrdersListType> => {
    const { data } = await axiosInstance.get<CardOrdersListType>('/card-orders');

    // Sort by orderDate descending (newest first)
    return {
        ...data,
        cardOrderHistory: data.cardOrderHistory.sort(
            (a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
        ),
    };
};

export const useCardOrders = () =>
    useApiQuery({
        queryKey: ['fetch-card-orders'],
        fetcher: fetchCardOrders,
        validator: CardOrdersList,
    });
